/**
 * mediaUploadSpecs — the single source of truth for what the media upload
 * pipeline accepts, what it does to a file, and what we therefore tell users.
 *
 * WHY THIS FILE EXISTS
 * Before this module the numbers lived in three places that could drift: the
 * client compressor (userImageService), the server's variant ladder
 * (thumbnailService), and hand-typed help copy. They did drift — the
 * destination-cover modal shipped copy quoting an 8 MB / 1920 px pipeline on a
 * surface governed by a 5 MB / 3000 px one. Everything user-facing now reads
 * from here, so guidance can't describe a pipeline we no longer run.
 *
 * WHEN YOU CHANGE A LIMIT
 * Change it here. `UserImageService.IMAGE_CONFIG` is this object, and
 * <MediaQualityHint> renders its numbers, so the compressor and the help text
 * move together. The one value that is NOT derived is the server variant
 * ladder — SERVER_PHOTO_VARIANT_WIDTHS mirrors SIZES in
 * server/services/thumbnailService.js and must be edited in both places.
 *
 * Video is the exception to "change it here": the server enforces its limits,
 * so they live in src/config/videoLimits.js (CommonJS, which the server can
 * require) and VIDEO_SPECS below reads them.
 */

import { VIDEO_STATUS as VIDEO_ROLLOUT, VIDEO_LIMITS } from '../config/videoLimits';

const KB = 1024;
const MB = 1024 * 1024;
const GB = 1024 * MB;

/**
 * Client-side compression targets, keyed by image type.
 *
 * Key names intentionally match what UserImageService.compressImage() reads
 * (maxWidth / maxHeight / quality / maxSize / format) so the service consumes
 * this object directly rather than re-mapping it.
 *
 * activity/trip/venue share one config — they're all "user-uploaded content
 * photos" and routinely cross-associate (a trip photo gets tied to a venue, an
 * activity photo lands in a recap album). Sized larger than Instagram's
 * ~1080px so trip memories preserve detail in the fullscreen gallery on retina
 * displays.
 */
export const PHOTO_SPECS = {
  profile: {
    maxSize: 200 * KB,
    maxWidth: 400,
    maxHeight: 400,
    quality: 0.8,
    format: 'image/jpeg'
  },
  activity: {
    maxSize: 5 * MB,
    maxWidth: 3000,
    maxHeight: 3000,
    quality: 0.92,
    format: 'image/jpeg'
  },
  trip: {
    maxSize: 5 * MB,
    maxWidth: 3000,
    maxHeight: 3000,
    quality: 0.92,
    format: 'image/jpeg'
  },
  venue: {
    maxSize: 5 * MB,
    maxWidth: 3000,
    maxHeight: 3000,
    quality: 0.92,
    format: 'image/jpeg'
  },
  // Profile banner — the server re-normalizes to fit 1600px and caps the
  // payload at 800KB (userImagesRoutes), so compress to just under both.
  banner: {
    maxSize: 800 * KB,
    maxWidth: 1600,
    maxHeight: 1600,
    quality: 0.85,
    format: 'image/jpeg'
  }
};

/**
 * Fallback for users on slow connections (2G/3G or Save-Data). Trades quality
 * for upload speed automatically — no user-facing toggle. Roughly
 * Instagram-tier output so upload times stay tolerable on cellular.
 *
 * User-visible consequence, surfaced in the hint: a photo uploaded on a weak
 * connection is capped well below the wifi ceiling.
 */
export const SLOW_CONNECTION_PHOTO_SPEC = {
  maxSize: 1 * MB,
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.85,
  format: 'image/jpeg'
};

/**
 * The image type used by every "photo of a place/trip/activity" surface —
 * i.e. what the quality hint describes unless a caller says otherwise.
 */
export const DEFAULT_CONTENT_PHOTO_TYPE = 'activity';

/**
 * Widths the server generates from the stored original.
 * MIRRORS server/services/thumbnailService.js SIZES — update both together.
 * Listed here only so the hint can explain why uploading beyond the cap is
 * wasted: every delivered variant is at or below the largest of these.
 */
export const SERVER_PHOTO_VARIANT_WIDTHS = [200, 600, 1200, 2400];

/**
 * Colour space the pipeline assumes. Compression draws through a canvas and
 * re-encodes; the source ICC profile does not survive intact, so a Display P3
 * or AdobeRGB export lands desaturated. This is the single highest-impact
 * export setting and the hint leads with it.
 */
export const PHOTO_COLOR_SPACE = 'sRGB';

/** Source formats the pickers accept. Everything is re-encoded to JPEG. */
export const ACCEPTED_PHOTO_FORMATS = ['JPEG', 'PNG', 'WebP', 'HEIC'];

// ---------------------------------------------------------------------------
// Video — NOT SHIPPED YET
// ---------------------------------------------------------------------------

/**
 * Lifecycle of the video pipeline, and the only switch the hint reads:
 * 'hidden' | 'coming_soon' | 'live'. Set in src/config/videoLimits.js, where
 * the server's upload gate reads the same value.
 */
export const VIDEO_STATUS = VIDEO_ROLLOUT;

/**
 * PROVISIONAL until video upload ships (V5): `provisional: true` makes the
 * hint render the "final limits confirmed at launch" caveat.
 *
 * The guidance stays codec-boring: footage shot to it today must still work
 * when the pipeline lands, so it names the universally safe container. The
 * upload also accepts MOV, HEVC and ProRes; the encoder (V3) probes the real
 * codec. The limits come from VIDEO_LIMITS, which the server enforces.
 */
export const VIDEO_SPECS = {
  provisional: true,
  container: 'MP4',
  codec: 'H.264',
  audioCodec: 'AAC',
  // What to shoot. 4K is accepted and downscaled — there is no benefit to
  // shooting beyond it, and a real cost in upload time on cellular.
  recommendedHeight: 1080,
  maxHeight: VIDEO_LIMITS.maxShortEdge,
  // Delivery is capped at 30 fps; 60 fps sources are accepted.
  frameRate: 30,
  maxSeconds: VIDEO_LIMITS.maxSeconds,
  maxSize: VIDEO_LIMITS.maxBytes,
  monthlyMinutes: VIDEO_LIMITS.monthlyMinutes
};

// ---------------------------------------------------------------------------
// Derived guidance — shaped for i18n interpolation, so no component does
// arithmetic on the constants above.
// ---------------------------------------------------------------------------

/** Human-readable byte size: 4294967296 -> "4 GB", 5242880 -> "5 MB", 204800 -> "200 KB". */
export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes >= GB) {
    const gb = bytes / GB;
    return (Number.isInteger(gb) ? gb : Math.round(gb * 10) / 10) + ' GB';
  }
  if (bytes >= MB) {
    const mb = bytes / MB;
    return (Number.isInteger(mb) ? mb : Math.round(mb * 10) / 10) + ' MB';
  }
  return Math.round(bytes / KB) + ' KB';
}

/** JPEG quality as the 0-100 number export dialogs use: 0.92 -> 92. */
export function qualityPercent(quality) {
  return Math.round(quality * 100);
}

/**
 * Everything the photo section of the hint needs, for one image type.
 * Falls back to the content-photo spec for an unknown type, matching
 * UserImageService.compressImage()'s own fallback.
 */
export function getPhotoGuidance(photoType = DEFAULT_CONTENT_PHOTO_TYPE) {
  const spec = PHOTO_SPECS[photoType] || PHOTO_SPECS[DEFAULT_CONTENT_PHOTO_TYPE];
  return {
    longEdge: Math.max(spec.maxWidth, spec.maxHeight),
    maxSizeLabel: formatBytes(spec.maxSize),
    quality: qualityPercent(spec.quality),
    colorSpace: PHOTO_COLOR_SPACE,
    formats: ACCEPTED_PHOTO_FORMATS.join(', '),
    slowConnectionLongEdge: Math.max(
      SLOW_CONNECTION_PHOTO_SPEC.maxWidth,
      SLOW_CONNECTION_PHOTO_SPEC.maxHeight
    )
  };
}

/** Everything the video section of the hint needs. */
export function getVideoGuidance() {
  return {
    container: VIDEO_SPECS.container,
    codec: VIDEO_SPECS.codec,
    audioCodec: VIDEO_SPECS.audioCodec,
    recommendedHeight: VIDEO_SPECS.recommendedHeight,
    maxHeight: VIDEO_SPECS.maxHeight,
    frameRate: VIDEO_SPECS.frameRate,
    maxMinutes: Math.round(VIDEO_SPECS.maxSeconds / 60),
    maxSizeLabel: formatBytes(VIDEO_SPECS.maxSize),
    monthlyMinutes: VIDEO_SPECS.monthlyMinutes,
    provisional: VIDEO_SPECS.provisional
  };
}

/** True when the hint should render a video section at all. */
export function shouldShowVideoGuidance() {
  return VIDEO_STATUS !== 'hidden';
}

/** True once video upload actually works (drops the "coming soon" badge). */
export function isVideoLive() {
  return VIDEO_STATUS === 'live';
}
